import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import dayJS from '@/shared/helpers/dayjs';
import * as Yup from 'yup';
import { Alert, Box, Button, Checkbox } from '@/shared/ui';
import { selectIsOnlyOneMedication, selectOnboardingUser } from '@/store/slices/onboarding/selectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths } from '@/shared/endpoints/customers';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { URLSearchParamsInit, useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { HueOption } from '../../constants';
import { useMediaQuery, useTheme } from '@mui/material';
import { setFixedBlock } from '@/store/slices/onboarding';
import { ROUTES } from '@/shared/constants/routes';
import { ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { ConfirmModal } from '@/shared/components/confirm-modal';
import { MedicationCard } from '../medbox/components/medications/medications-list/card';
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook';
import { GtmEvent } from '@/shared/types/enums';
import saleForceWebHookInstance from '@/services/saleforce';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { _SplitLayout } from '@/modules/onboarding/components/split-layout';
import { completeOnboardingNotesRequest, cropBase64ImageWithLine } from './helpers';
import { convertedMedications, IPrescribables } from '@/shared/helpers/main';
import SignatureCanvas from 'react-signature-canvas'
import SignHereIcon from '@/assets/icons/x-icon-green.svg';
import { uploadSignature } from '../insurance/components/upload-form/helpers';
import { useFormik } from 'formik';
import useDeviceType from '@/shared/hooks/useDeviceType';
import HippacIcon from '@/assets/images/onboarding/hipaa.png';
import EncryptionIcon from '@/assets/images/onboarding/encryption.png';
import { useHeaderHeight } from '@/shared/components/header-height-context';

import { reviewInitialValues } from '@/modules/onboarding/pages/review/constants';
import { usePrevious } from '@/shared/hooks/previous';

interface Props {
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
}

const validationSchema = Yup.object().shape({
    acceptSubmitting: Yup.boolean()
        .oneOf([true], ERROR_MESSAGES.REQUIRED_FIELD_MISSING),
    base64EncodedImage: Yup.string()
        .test('base64Required', ERROR_MESSAGES.REQUIRED_FIELD_MISSING, function (value) {
            const { acceptSubmitting } = this.parent;
            if (acceptSubmitting && !value) {
                return false;
            }
            return true;
        })
});

const Review = ({ navigateToStep }: Props) => {
    const theme = useTheme();
    const ref = useRef(null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const sendDataToGTM = useGTMDispatch();
    const headerHeight = useHeaderHeight();
    const currentUser = useAppSelector(selectOnboardingUser);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isOnlyOneMedication = useAppSelector(selectIsOnlyOneMedication);
    const sigCanvasRef = useRef(null);
    const containerRef = useRef(null);
    const [completeLoading, setCompleteLoading] = useState(false);
    const [canvasWidth, setCanvasWidth] = useState(500); // Default width
    const { deviceType } = useDeviceType();
    const isDesktop = deviceType === 'desktop';

    const formik = useFormik({
        initialValues: reviewInitialValues,
        validationSchema: validationSchema,
        onSubmit: () => {
            handleSubmit();
        }
    });

    const prevAcceptSubmitting = usePrevious(formik.values.acceptSubmitting);

    const trim = async () => {
        console.log(sigCanvasRef?.current, 'asdfasdfasdfasdf');
        const base64EncodedImage = sigCanvasRef?.current?.toDataURL('image/png');
        formik.setFieldValue('base64EncodedImage', base64EncodedImage);
    }

    const clear = useCallback(() => {
        sigCanvasRef?.current?.clear();
        formik.setFieldValue('base64EncodedImage', '');
        formik.setFieldTouched('base64EncodedImage', false);
        formik.setFieldError('base64EncodedImage', '');
    }, [])

    useEffect(() => {
        // Set canvas width based on parent div
        const updateCanvasSize = () => {
            if (containerRef.current) {
                containerRef?.current?.offsetWidth && setCanvasWidth(containerRef.current.offsetWidth);
            }
        };

        updateCanvasSize(); // Initial set
        window.addEventListener("resize", updateCanvasSize); // Update on resize

        return () => window.removeEventListener("resize", updateCanvasSize);
    }, []);

    const prescribables = useMemo((): IPrescribables => convertedMedications(currentUser), [currentUser]);

    const allMedications = useMemo(() => [...prescribables.includingMedbox, ...prescribables.packagedSeparately], [prescribables]);

    const triggerOnboardingCompletedSfWebhook = useCallback(async (email: string) => {
        try {
            await saleForceWebHookInstance.post({
                email,
                phone: '',
                activation: 'activation2'
            });
        } catch (error) {
            console.error('Error in SaleForceWebHook:', error);
        }
    }, []);

    const sendDataToGTMForPrescriptions = useCallback(() => {
        const otcCount =
            prescribables.includingMedbox.filter(item => item.otc).length +
            prescribables.packagedSeparately.filter(item => item.otc).length;
        const prescriptionCount = prescribables.includingMedbox.length + prescribables.packagedSeparately.length - otcCount;
        sendDataToGTM({
            event: GtmEvent.PRESCRIPTION_ADDED,
            userId: currentUser?.userProfile?.accountId,
            prescriptionCount,
            otcCount,
            final: true
        });
    }, [currentUser?.userProfile?.accountId, prescribables, sendDataToGTM]);

    const sendDataToGTMForOnboardingCompleted = useCallback(() => {
        sendDataToGTM({ event: GtmEvent.ONBOARDING_COMPLETED, userId: currentUser?.userProfile?.accountId });
    }, [currentUser?.userProfile?.accountId, sendDataToGTM]);

    const handleComplete = useCallback(async () => {
        if (completeLoading) return;
        setCompleteLoading(true);

        try {
            const croppedAndDatedSig = await cropBase64ImageWithLine(formik.values.base64EncodedImage);
            if (!croppedAndDatedSig) {
                throw new Error('Signature is empty');
            }

            const uploadRes = await dispatch(
                uploadSignature(croppedAndDatedSig)
            );

            if (uploadRes.meta.requestStatus === 'rejected') {
                throw new Error('Upload failed');
            }

            const finishRes = await dispatch(CustomersController.onFinishOnboarding());

            if (finishRes.meta.requestStatus === 'rejected') {
                throw new Error('Onboarding finish failed');
            }

            completeOnboardingNotesRequest(currentUser);

            if (currentUser?.userOnboarding) {
                const payload = cloneDeep(currentUser.userOnboarding);
                payload.details = {
                    ...payload.details,
                    currentStep: CustomersStepTypeEnum.ThankYou,
                };

                await dispatch(CustomersController.updateMetadata(payload));

                sendDataToGTMForPrescriptions();
                sendDataToGTMForOnboardingCompleted();
                triggerOnboardingCompletedSfWebhook(currentUser?.email);

                navigate(ROUTES.ONBOARDING.THANK_YOU);
            }
        } catch (error) {
            toast.error(ERROR_MESSAGES.DEFAULT);
        } finally {
            setCompleteLoading(false);
        }
    }, [completeLoading, currentUser, dispatch, formik.values.base64EncodedImage, navigate, sendDataToGTMForOnboardingCompleted, sendDataToGTMForPrescriptions, triggerOnboardingCompletedSfWebhook]);



    const handleSubmit = useCallback(() => {
        handleComplete();
    }, [handleComplete]);

    const selectedHue = useMemo(() => {
        if (!currentUser) return;
        return HueOption.find(hue => hue.id === currentUser.userOnboarding?.details?.medboxHueType);
    }, [currentUser]);

    const handleOnDelete = useCallback(async () => {
        if (allMedications.length === 1) {
            if (currentUser?.userOnboarding) {
                const payload = cloneDeep(currentUser.userOnboarding);
                payload.details = { ...payload.dfetails, currentStep: CustomersStepTypeEnum.Prescriptions };
                await dispatch(CustomersController.updateMetadata(payload));
            }
            if (navigateToStep) {
                navigateToStep(CustomersStepTypeEnumPaths.PRESCRIPTIONS);
            } else {
                navigate(
                    `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDICATIONS}`
                );
            }
        }
    }, [allMedications.length, dispatch, navigateToStep, currentUser?.userOnboarding, navigate]);

    const onHueColorChange = () => {
        if (navigateToStep) {
            navigateToStep(CustomersStepTypeEnumPaths.PRESCRIPTIONS, -1, { fromReview: '1' });
        } else {
            navigate(
                `/${ROUTES.ONBOARDING.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MAIN}/${ROUTES.ONBOARDING.PRESCRIPTIONS.MEDBOX_HUE}?from=review`
            );
        }
    };

    useEffect(() => {
        if (ref.current) {
            dispatch(setFixedBlock(ref.current));
        }
        return () => {
            dispatch(setFixedBlock(undefined));
        };
    }, [ref, dispatch]);

    useEffect(() => {
        prevAcceptSubmitting !== undefined && clear();
    }, [prevAcceptSubmitting, formik.values.acceptSubmitting]);

    const acceptCheckboxTouchedAndError = formik.touched.acceptSubmitting && Boolean(formik.errors.acceptSubmitting);
    const sigTouchedAndError = formik.touched.base64EncodedImage && Boolean(formik.errors.base64EncodedImage);
    const lastMedicationAlertString = `You are about to remove your ${isOnlyOneMedication ? '' : 'last'} medication. This will take you back to the “My MedBox” section.`;

    // feat scroll to first encountered error
    const checkForErrorsAndScroll = () => {
        const element = document.querySelector(`.alert__error, .border-red-500`);

        if (!element) return;

        const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight - 80;
    
        setTimeout(() => {
            window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
            });
        }, 200)
    }

    return (
        currentUser && (
            <>
                <div className="mb-[2.1875rem] flex justify-center">
                    <h2 data-testid="review-title" className="review__heading text-center font-agrandir text-2xl md:text-3xl lg:text-left xl:text-5xl">
                        Let’s Review <span className="text-primary-700">Your MedBox</span>
                    </h2>
                </div>
                <_SplitLayout
                    dataTestid="review-layout-container"
                    customLeftClassNames={['!pl-0', 'xl-max:mb-[1rem]']}
                    sideContent={
                        <Box
                            data-testid="review-content-container"
                            bgWhite
                            sx={{
                                gap: '28px',
                                padding: '30px !important',
                                boxShadow: '0px 4px 7.1px 0px #0000000F',
                                flex: '1 1 auto',
                                borderRadius: '20px',
                                maxWidth: '647px',

                                [theme.breakpoints.up('xl')]: {
                                    width: 'calc(100% - 1.3125rem)',
                                    marginRight: '1.3125rem'
                                },

                                [theme.breakpoints.down('xl')]: {
                                    width: '100%',
                                    maxWidth: '100%'
                                },

                                [theme.breakpoints.down('sm')]: {
                                    padding: '20px !important'
                                },
                                '@media only screen and (min-width: 1441px)': {
                                    maxWidth: '700px'
                                }
                            }}
                        >
                            <div className="flex justify-between border-b-1 border-mint-200 pb-7">
                                <div className="w-36 sm:w-64">
                                    <h4 data-testid="review-user-name-text" className="pb-5 font-atkinsonHyperlegible text-lg sm:text-2xl lg:text-3xl">
                                        {`${currentUser?.userProfile?.firstName} ${currentUser?.userProfile?.lastName}`}’s MedBox
                                    </h4>
                                    {currentUser.addresses?.length && currentUser.addresses[0].fullAddress && (
                                        <div className="flex flex-col gap-1">
                                            <span data-testid="review-shipping-address-text" className="font-atkinsonBold text-sm uppercase md:text-base">Shipping Address:</span>
                                            <div data-testid="review-shipping-address-content" className="flex-col font-atkinsonHyperlegible text-sm md:text-base">
                                                <p data-testid="review-shipping-address-addressLine1">{`${currentUser.addresses[0].addressLine1} ${currentUser.addresses[0].addressLine2}`}</p>
                                                {currentUser.addresses[0].unit && <p data-testid="review-shipping-address-unit">{currentUser.addresses[0].unit}</p>}
                                                <p data-testid="review-shipping-address-city-state">{`${currentUser.addresses[0].city}, ${currentUser.addresses[0].state}`}</p>
                                                <p data-testid="review-shipping-address-zip">{`${currentUser.addresses[0].zip}`}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div data-testid="review-medbox-hue-container" className="box-border h-max w-[174px] overflow-hidden rounded-10 ms-max:w-[120px] md-max:w-[138px]">
                                    <div data-testid="review-medbox-hue" className="relative h-[150px] overflow-hidden md-max:h-[100px]">
                                        <Button
                                            className="review__edit_color"
                                            color="primary"
                                            size="small"
                                            sx={{
                                                padding: '1px 10px !important',
                                                border: 'inherit',
                                                background: '#003D3380',
                                                fontFamily: 'Atkinson',
                                                position: 'absolute',
                                                right: '7px',
                                                top: '7px',
                                                zIndex: 3,
                                                fontSize: '15px',
                                                [theme.breakpoints.down('md')]: {
                                                    padding: '1px 9px !important',
                                                    fontSize: '14px'
                                                },
                                                ':hover': {
                                                    bgcolor: 'primary.700'
                                                }
                                            }}
                                            onClick={onHueColorChange}
                                            testId="button-review-edit-color"
                                        >
                                            Edit Color
                                        </Button>
                                        <img data-testid="review-medbox-hue-image" src={selectedHue?.src} className="relative object-center" />
                                    </div>
                                    <div data-testid="review-medbox-hue-name" className="flex items-center justify-center bg-primary-700 py-1 font-dmSans text-base text-white md:text-lg">
                                        {selectedHue?.name}
                                    </div>
                                </div>
                            </div>

                            <div className="w-full">
                                {prescribables.includingMedbox.length ? (
                                    <div data-testid="review-including-medbox-container" className="flex flex-col gap-3 pt-5 md:pt-9">
                                        <h3 data-testid="review-including-medbox-text" className="font-atkinsonBold text-lg">In Your MedBox</h3>
                                        <div className="flex flex-col gap-4">
                                            {prescribables.includingMedbox.map((e, idx) => (
                                                <MedicalCard
                                                    key={idx + 'includingMedbox'}
                                                    medication={e}
                                                    onDelete={handleOnDelete}
                                                    deletionModalText={allMedications.length === 1 ? lastMedicationAlertString : ''}
                                                    testId="medical-card-includingMedbox"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                                {prescribables.packagedSeparately.length ? (
                                    <div data-testid="review-packaged-separately-container" className="flex flex-col gap-3 pt-5 md:pt-7">
                                        <h3 data-testid="review-packaged-separately-text" className="font-atkinsonBold text-lg">Packaged Separately</h3>
                                        <div className="flex flex-col gap-4">
                                            {prescribables.packagedSeparately.map((e, idx) => (
                                                <MedicalCard
                                                    key={idx + 'packagedSeparately'}
                                                    medication={e}
                                                    onDelete={handleOnDelete}
                                                    deletionModalText={allMedications.length === 1 ? lastMedicationAlertString : ''}
                                                    testId="medical-card-packagedSeparately"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </Box>
                    }
                    customRightClassNames={['xl-max:w-full']}
                    mainContent={
                        <Box
                            ref={ref}
                            bgWhite
                            data-testid="review-submit-block-container"
                            sx={{
                                width: '100%',
                                justifyContent: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                boxShadow: '0px 4px 7.1px 0px #0000000F',
                                borderRadius: '20px',
                                height: 'max-content',
                                padding: '30px !important',
                                [theme.breakpoints.down('sm')]: {
                                    padding: '15px 20px 30px 20px !important',
                                },
                            }}
                            className="!4xl:w-[640px] mb-4 lg:mb-0"
                        >

                            <div className='flex flex-col justify-center gap-[10px] items-center max-w-[700px] w-full'>
                                {acceptCheckboxTouchedAndError && (
                                    <Alert testId="should-accept-terms-alert" boldLabel={false} label="You must accept terms before signing & submitting." />
                                )}
                                <Checkbox
                                    testId="review-signature-confirm" 
                                    label={
                                        <span className='relative -top-1 font-atkinsonHyperlegible text-[16px] sm:text-[18px]'>
                                            <span data-testid="review-signature-confirm-content-text" className="block sm-max:leading-5">
                                                By signing, I acknowledge that I am switching my pharmacy to AmeriPharma MedBox and authorize AmeriPharma MedBox to initiate the transfer of my prescriptions. I understand that my electronic signature is legally binding.
                                            </span>
                                            <span data-testid="review-signature-confirm-bottom-text" className='block mt-2'>
                                                I have read and agree to the <a data-testid="review-signature-confirm-terms-link" href="https://medbox.com/terms-of-use/" target="_blank" rel="noopener noreferrer">
                                                    <span data-testid="review-signature-configm-terms-text" style={{ fontWeight: '400' }} className="text-primary underline">Terms of Use</span>
                                                </a>.
                                            </span>
                                        </span>
                                    }
                                    className={`revew__checkbox !items-start !gap-[13px] border p-5 rounded-2xl ${acceptCheckboxTouchedAndError ? 'border-red-500' : formik.values.acceptSubmitting ? 'border-primary-400' : 'border-gray-300'
                                        }`}
                                    required
                                    name="acceptSubmitting"
                                    error={acceptCheckboxTouchedAndError}
                                    checked={formik.values.acceptSubmitting}
                                    onChange={formik.handleChange}
                                    labelClassName="font-atkinsonHyperlegible !text-[17px] text-[#000]"
                                />
                                {formik.values.acceptSubmitting && <div className="flex w-full flex-col justify-start items-start font-atkinsonHyperlegible">
                                    <span data-testid="review-signature-title" className="block text-20 sm:text-30">Draw Signature</span>
                                    <span data-testid="review-signature-subtitle" className='text-14 sm:text-18'>{`${isDesktop ? 'User your mouse to sign.' : 'Use your finger to sign.'}`}</span>
                                </div>}

                                <div className={`flex-1 ${formik.values.acceptSubmitting ? 'bg-grey-100' : 'bg-grey-110'} bg-opacity-60 p-2 rounded-md relative border border-1 ${sigTouchedAndError ? 'border-2 border-red-500' : 'border-gray-110'} w-full`}>
                                    {!formik.values.acceptSubmitting && (
                                        <div className="absolute inset-0 bg-grey-120 z-[99] pointer-events-auto rounded-md"></div>
                                    )}
                                    <div
                                        className="h-[170px] w-full overflow-hidden rounded-md"
                                    >
                                        <div data-testid="review-signature-canvas-container" className='flex items-center justify-center'>
                                            <SignatureCanvas
                                                clearOnResize={false}
                                                onEnd={trim}
                                                ref={sigCanvasRef}
                                                canvasProps={{
                                                    style: { width: canvasWidth, height: '170px' },
                                                }}
                                            />
                                        </div>
                                        <div className={`absolute bottom-1/3 left-[2px] flex items-end w-full gap-2 max-w-[700px] px-2 ${formik.values.acceptSubmitting ? '' : 'opacity-40'}`}>
                                            <img className="h-[10px] w-[10px] sm:h-[13px] sm:w-[13px]" src={SignHereIcon} alt="Sign Here" />
                                            <div ref={containerRef} className="flex-1 border-b border-[#003D33]"></div>
                                            <div className='h-[14px] w-[14px] sm:h-[10px] sm:w-[10px]' />
                                        </div>
                                    </div>
                                </div>
                                {formik.values.acceptSubmitting && <div className="flex flex-row justify-between items-center w-full">
                                    <span data-testid="review-signature-date" className="font-atkinson text-black text-15 sm:text-16">{dayJS().format('MMM D, YYYY')}</span>
                                    <span data-testid="button-review-signature-reset" onClick={clear} className='font-dmSansMedium underline cursor-pointer text-primary-400 text-17'>Reset Signature</span>
                                </div>}
                                {sigTouchedAndError && (
                                    <Alert testId="review-sign-required-alert" boldLabel={false} label="You must sign before submitting." />
                                )}
                                <div className={`w-full ${isMobile ? 'px-5 pt-[15px] pb-[30px] bg-white w-full fixed left-0 bottom-[60px] z-[11] shadow-reviewSubmit rounded-t-[15px]' : ''}`}>
                                    <Button
                                        testId="button-review-submit"
                                        disabled={completeLoading}
                                        loading={completeLoading}
                                        showEndIcon
                                        className="revew__submit"
                                        sx={{ mt: !isMobile ? '10px' : '0', width: '100%', boxShadow: '0px 4px 7.1px 0px #0000000F' }}
                                        onClick={() => {
                                            formik.handleSubmit();
                                            setTimeout(checkForErrorsAndScroll, 200);
                                        }}
                                    >
                                        Sign & Submit
                                    </Button>
                                </div>

                                <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-4 mt-[15px] ">
                                    <img src={HippacIcon} alt="" className="h-12 max-w-25" />
                                    <img src={EncryptionIcon} alt="" className="h-12 max-w-25" />
                                </div>
                            </div>

                            <div className="shadow-appNavbar"></div>
                        </Box >
                    }
                />
            </>
        )
    );
};

const MedicalCard = ({
    medication,
    onDelete,
    deletionModalText,
    testId = 'medical-card',
}: {
    medication: ITransferPrescriptions;
    onDelete?: () => void;
    deletionModalText?: string;
    testId?: string;
}) => {
    const dispatch = useAppDispatch();
    const [deleteModalShown, setDeleteModalShown] = useState(false);

    const deleteMedication = () => {
        dispatch(CustomersController.removeTransferPrescription(medication)).then(() => {
            onDelete?.();
        });
        setDeleteModalShown(false);
    };

    return (
        <Box
            data-testid={`${testId}-container`}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap={'wrap'}
            gap={2}
            borderRadius={4}
            border={'1px solid black'}
            sx={{ backgroundColor: '#F7FAF8FF', border: '1px solid black', width: '100%', p: '25px 25px !important' }}
        >
            <div className="flex items-center gap-2.5">
                <span
                    className="font-atkinsonBold text-base uppercase text-dark md:text-lg"
                    data-testid={`${testId}-prescribable-name`}
                >{medication.prescribable.name}</span>
            </div>
            <div className="flex flex-grow justify-end">
                <Button
                    className={`review__delete_${medication.prescribable.name}`}
                    variant="outlined"
                    sx={{
                        fontSize: '16px',
                        p: '2px 0 !important',
                        fontFamily: 'Atkinson !important',
                        borderWidth: '2px !important',
                        minWidth: '77px'
                    }}
                    onClick={() => setDeleteModalShown(true)}
                    testId={`button-${testId}-delete`}
                >
                    Delete
                </Button>
            </div>
            <ConfirmModal
                open={!!deleteModalShown}
                onCancel={() => setDeleteModalShown(false)}
                onConfirm={deleteMedication}
                title={deletionModalText || 'Are you sure you want to remove this medication?'}
                confirmText="Yes, Remove"
                cancelText="Cancel Removal"
            >
                <MedicationCard medication={medication} showActions={false} />
            </ConfirmModal>
        </Box>
    );
};

export default withScrollRestoration(Review);
